import React, { Component, PureComponent, useEffect, useState } from 'react';
import { PatientHealthSummaryWithPoolingResponse } from '../../../api/getPatientHealthSummaryWithPooling';
import { Popover } from 'antd';
import moment from 'moment';
import {
    FeedbackType,
    sendFeedbackToSlack,
} from '../../../components/common/PatientHealthSummary/Utils';
import { getPatient } from '../../../store/selectors/prescriptionPad';
import { connect } from 'react-redux';
import { GlobalState } from '../../../store/reducers';
import { Patient } from '../../../store/types/prescription';
import FeedbackPopover from '../../../components/common/PatientHealthSummary/FeedbackPopover';
import { track } from '../../../tracking';
import fetchDocAssistWordSummary, {
    AssessmentSummaryResponse,
    Medicine,
    TimeInfo,
    UpdatedMedicalProfile,
    Vitals,
} from '../../../api/fetchDocAssistWordSummary';

type Props = {
    patientHealthSummary: PatientHealthSummaryWithPoolingResponse;
    patient?: Patient;
    from: string;
};

type State = {
    openPopOver?: string;
};

export class PatientSummaryMagicBox extends PureComponent<Props, State> {
    state: State = {
        openPopOver: undefined,
    };

    clear = () => {
        this.setState({ openPopOver: undefined });
    };

    onFeedback = (type: FeedbackType, input?: string): void => {
        const { patient, from } = this.props;
        sendFeedbackToSlack(type, patient?.id || '', input || '', from);
    };

    setPopOver = (popOver: string) => {
        this.setState({ openPopOver: popOver });
    };

    HighlightEmTag() {
        const { openPopOver } = this.state;
        const text = this.props.patientHealthSummary.most_recent_summary?.six_month_overview;
        // Split the text into parts based on the <em> tag
        const parts = text?.split(/(<em>.*?<\/em>)/);

        // Loop through each part and render them
        const renderedText = parts?.map((part, index) => {
            // Check if the part contains <em> tag
            if (part.includes('<em>')) {
                const cleanText = part.replace(/<\/?em>/g, '');

                return (
                    <PatientSummaryDescription
                        key={cleanText}
                        cleanText={cleanText}
                        patientHealthSummary={this.props.patientHealthSummary}
                        onFeedback={this.onFeedback}
                        openPopOver={openPopOver}
                        setPopOver={this.setPopOver}
                        clear={this.clear}
                        patientId={this.props?.patient?.id || ''}
                    />
                );
            } else {
                return <span key={index}>{part}</span>;
            }
        });

        return <div>{renderedText}</div>;
    }

    render() {
        return (
            <div className="pb-8 px-8 space-y-8">
                <div className="text-13">{this.HighlightEmTag()}</div>
                <div className="flex items-center justify-between rounded-b-7">
                    <div className="flex items-center space-x-4">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                        >
                            <g
                                style={{
                                    mixBlendMode: 'multiply',
                                }}
                            >
                                <circle cx="8.61533" cy="8.07602" r="5.38462" fill="#215FFF" />
                            </g>
                            <g filter="url(#filter0_b_45_2979)">
                                <circle
                                    cx="3.76923"
                                    cy="3.76923"
                                    r="3.76923"
                                    fill="#B45EDC"
                                    fill-opacity="0.6"
                                />
                            </g>
                            <defs>
                                <filter
                                    id="filter0_b_45_2979"
                                    x="-1.07692"
                                    y="-1.07692"
                                    width="9.69242"
                                    height="9.69291"
                                    filterUnits="userSpaceOnUse"
                                    color-interpolation-filters="sRGB"
                                >
                                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                                    <feGaussianBlur
                                        in="BackgroundImageFix"
                                        stdDeviation="0.538462"
                                    />
                                    <feComposite
                                        in2="SourceAlpha"
                                        operator="in"
                                        result="effect1_backgroundBlur_45_2979"
                                    />
                                    <feBlend
                                        mode="normal"
                                        in="SourceGraphic"
                                        in2="effect1_backgroundBlur_45_2979"
                                        result="shape"
                                    />
                                </filter>
                            </defs>
                        </svg>
                        <span className="text-11 text-darwin-neutral-400 whitespace-nowrap">
                            Powered by Eka DocAssist AI
                        </span>
                    </div>

                    <div className="flex items-center space-x-6">
                        <button
                            className="p-4 bg-darwin-neutrals-50 rounded-4 cursor-pointer"
                            onClick={() => {
                                this.onFeedback('positive');
                            }}
                        >
                            <svg
                                className="w-14 h-13"
                                viewBox="0 0 14 12"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="#A3A3A3"
                            >
                                <path d="M8.70117 0.513672C9.69141 0.792969 10.25 1.80859 9.9707 2.77344L9.81836 3.28125C9.74219 3.56055 9.64062 3.83984 9.51367 4.09375H11.875C12.7637 4.09375 13.5 4.83008 13.5 5.71875C13.5 6.17578 13.2969 6.58203 13.0176 6.88672C13.0684 7.03906 13.0938 7.19141 13.0938 7.36914C13.0938 7.87695 12.8398 8.35938 12.4336 8.66406C12.459 8.76562 12.4844 8.86719 12.4844 8.99414C12.4844 9.50195 12.2305 9.95898 11.875 10.2637C11.8242 11.127 11.1133 11.8379 10.25 11.8379H7.76172C7.20312 11.8379 6.64453 11.6602 6.1875 11.3555L4.61328 10.3145C4.33398 10.1113 4.25781 9.73047 4.46094 9.45117C4.63867 9.17188 5.01953 9.0957 5.29883 9.29883L6.87305 10.3398C7.12695 10.5176 7.43164 10.5938 7.76172 10.5938H10.25C10.4531 10.5938 10.6562 10.416 10.6562 10.1875C10.6562 10.1621 10.6309 10.1113 10.6309 10.0859C10.5547 9.78125 10.707 9.47656 10.9863 9.34961C11.1387 9.29883 11.2656 9.14648 11.2656 8.96875C11.2656 8.89258 11.2148 8.79102 11.1641 8.74023C11.0371 8.5625 11.0117 8.33398 11.0879 8.13086C11.1387 7.92773 11.3164 7.80078 11.5449 7.75C11.7227 7.72461 11.875 7.54688 11.875 7.34375C11.875 7.24219 11.8242 7.16602 11.7734 7.08984C11.6211 6.9375 11.5703 6.73438 11.6211 6.53125C11.6719 6.32812 11.8242 6.17578 12.002 6.09961C12.1543 6.04883 12.2812 5.89648 12.2812 5.71875C12.2812 5.51562 12.0781 5.3125 11.875 5.3125H8.47266C8.24414 5.3125 8.04102 5.18555 7.93945 4.98242C7.83789 4.7793 7.83789 4.55078 7.96484 4.37305C8.26953 3.94141 8.49805 3.45898 8.65039 2.95117L8.80273 2.44336C8.87891 2.11328 8.70117 1.7832 8.37109 1.68164C8.04102 1.60547 7.71094 1.7832 7.60938 2.11328L7.48242 2.62109C7.33008 3.12891 7.07617 3.58594 6.7207 3.99219L5.40039 5.41406C5.17188 5.66797 4.79102 5.69336 4.53711 5.46484C4.30859 5.23633 4.2832 4.85547 4.51172 4.60156L5.80664 3.17969C6.03516 2.92578 6.21289 2.62109 6.31445 2.29102L6.44141 1.7832C6.7207 0.792969 7.73633 0.234375 8.70117 0.513672ZM1.3125 4.5H2.9375C3.36914 4.5 3.75 4.88086 3.75 5.3125V11C3.75 11.457 3.36914 11.8125 2.9375 11.8125H1.3125C0.855469 11.8125 0.5 11.457 0.5 11V5.3125C0.5 4.88086 0.855469 4.5 1.3125 4.5Z" />
                            </svg>
                        </button>
                        <FeedbackPopover
                            onFeedback={this.onFeedback}
                            removePopupContainer
                            from="patient-summary-magic-box"
                            patientId={this.props.patient?.id || ''}
                        >
                            <button className="p-4 bg-darwin-neutrals-50 rounded-4 cursor-pointer">
                                <svg
                                    className="w-14 h-13"
                                    viewBox="0 0 14 13"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="#A3A3A3"
                                >
                                    <path d="M8.70117 11.7617C7.73633 12.041 6.7207 11.457 6.44141 10.4922L6.31445 9.98438C6.21289 9.6543 6.03516 9.34961 5.80664 9.0957L4.51172 7.67383C4.2832 7.41992 4.30859 7.03906 4.53711 6.81055C4.79102 6.58203 5.17188 6.60742 5.40039 6.86133L6.7207 8.2832C7.07617 8.66406 7.33008 9.14648 7.48242 9.6543L7.60938 10.1621C7.71094 10.4922 8.04102 10.6699 8.37109 10.5938C8.70117 10.4922 8.87891 10.1621 8.80273 9.83203L8.65039 9.32422C8.49805 8.81641 8.26953 8.33398 7.96484 7.90234C7.83789 7.72461 7.83789 7.4707 7.93945 7.26758C8.04102 7.06445 8.24414 6.9375 8.47266 6.9375H11.875C12.0781 6.9375 12.2812 6.75977 12.2812 6.53125C12.2812 6.37891 12.1543 6.22656 12.002 6.15039C11.8242 6.09961 11.6719 5.92188 11.6211 5.74414C11.5703 5.54102 11.6211 5.33789 11.7734 5.18555C11.8242 5.10938 11.875 5.0332 11.875 4.90625C11.875 4.72852 11.7227 4.55078 11.5449 4.52539C11.3164 4.47461 11.1387 4.32227 11.0879 4.14453C11.0117 3.94141 11.0371 3.71289 11.1641 3.53516C11.2148 3.48438 11.2656 3.38281 11.2656 3.28125C11.2656 3.12891 11.1387 2.97656 10.9863 2.90039C10.707 2.79883 10.5547 2.49414 10.6309 2.18945C10.6309 2.13867 10.6562 2.11328 10.6562 2.0625C10.6562 1.85938 10.4531 1.65625 10.25 1.65625H7.76172C7.43164 1.65625 7.12695 1.75781 6.87305 1.93555L5.29883 2.97656C5.01953 3.17969 4.63867 3.10352 4.46094 2.79883C4.25781 2.51953 4.33398 2.16406 4.61328 1.96094L6.1875 0.919922C6.64453 0.615234 7.20312 0.4375 7.76172 0.4375H10.25C11.1133 0.4375 11.8242 1.14844 11.875 2.01172C12.2305 2.31641 12.4844 2.77344 12.4844 3.28125C12.4844 3.4082 12.459 3.50977 12.4336 3.61133C12.8398 3.91602 13.0938 4.37305 13.0938 4.90625C13.0938 5.08398 13.0684 5.23633 13.0176 5.38867C13.2969 5.69336 13.5 6.09961 13.5 6.53125C13.5 7.44531 12.7637 8.15625 11.875 8.15625H9.51367C9.64062 8.43555 9.74219 8.71484 9.81836 8.99414L9.9707 9.50195C10.25 10.4668 9.69141 11.4824 8.70117 11.7617ZM1.3125 9.375C0.855469 9.375 0.5 9.01953 0.5 8.5625V2.875C0.5 2.44336 0.855469 2.0625 1.3125 2.0625H2.9375C3.36914 2.0625 3.75 2.44336 3.75 2.875V8.5625C3.75 9.01953 3.36914 9.375 2.9375 9.375H1.3125Z" />
                                </svg>
                            </button>
                        </FeedbackPopover>
                    </div>
                </div>
            </div>
        );
    }
}

export default connect((state: GlobalState) => ({
    patient: getPatient(state),
}))(PatientSummaryMagicBox);

type MedicationArray = {
    medicines?: Medicine[];
    diffDate?: string;
    type: string;
} & TimeInfo;

type VitalsArray = {
    vitals?: Vitals[];
    diffDate?: string;
} & TimeInfo;

const PatientSummaryDescription = ({
    cleanText,
    patientHealthSummary,
    onFeedback,
    openPopOver,
    setPopOver,
    clear,
    patientId,
}: {
    cleanText: string;
    patientHealthSummary: PatientHealthSummaryWithPoolingResponse;
    onFeedback: (type: FeedbackType, input?: string) => void;
    openPopOver?: string;
    setPopOver?: (popOver: string) => void;
    clear: () => void;
    patientId: string;
}) => {
    const [medicalProfile, setMedicalProfile] = useState<AssessmentSummaryResponse | undefined>(
        undefined,
    );

    const fetchSummary = () => {
        if (
            patientHealthSummary?.information_hash &&
            patientHealthSummary?.most_recent_summary?.model_id &&
            !medicalProfile
        ) {
            fetchDocAssistWordSummary(
                cleanText,
                patientHealthSummary?.information_hash,
                patientId,
                patientHealthSummary?.most_recent_summary?.model_id,
            )
                .then((res) => {
                    setMedicalProfile(res);
                })
                .catch((err) => {
                    console.error(err);
                    setMedicalProfile(undefined);
                });
        }
    };

    const medicationArray = [
        {
            medicines: medicalProfile?.therapy_regime?.current?.medicines,
            diffDate: (() => {
                if (!medicalProfile?.therapy_regime?.current?.time_info?.start) {
                    return undefined;
                }

                const startDate = moment(medicalProfile?.therapy_regime?.current?.time_info?.start);
                const endDate = moment(medicalProfile?.therapy_regime?.current.time_info.end);

                const diffInMonths = endDate.diff(startDate, 'months');
                if (diffInMonths < 1) {
                    const diffInDays = endDate.diff(startDate, 'days');
                    return diffInDays + ' days';
                } else {
                    return diffInMonths + ' months';
                }
            })(),
            ...medicalProfile?.therapy_regime?.current?.time_info,
            type: 'current',
        },
        ...(medicalProfile?.therapy_regime?.history || []).map((h) => {
            return {
                medicines: h?.medicines,
                diffDate: (() => {
                    if (!h?.time_info?.start) {
                        return undefined;
                    }

                    const startDate = moment(h?.time_info.start);
                    const endDate = moment(h?.time_info.end);

                    const diffInMonths = endDate.diff(startDate, 'months');

                    if (diffInMonths < 1) {
                        const diffInDays = endDate.diff(startDate, 'days');
                        return diffInDays + ' days';
                    } else {
                        return diffInMonths + ' months';
                    }
                })(),
                ...h?.time_info,
                type: 'history',
            };
        }),
    ];

    const vitalsArray = [
        ...(medicalProfile?.vital_status || []).map((h) => {
            return {
                vitals: h?.vitals,
                diffDate: (() => {
                    if (!h?.time_info?.start) {
                        return undefined;
                    }
                    const startDate = moment(h?.time_info.start);
                    const endDate = moment(h?.time_info.end);

                    const diffInMonths = endDate.diff(startDate, 'months');

                    if (diffInMonths < 1) {
                        const diffInDays = endDate.diff(startDate, 'days');
                        return diffInDays + ' days';
                    } else {
                        return diffInMonths + ' months';
                    }
                })(),
                ...h?.time_info,
            };
        }),
    ];

    const id = cleanText?.toLowerCase();

    return (
        <>
            <span
                style={{
                    fontWeight: '700',
                    borderRadius: '4px',
                    padding: '0px 2px',
                    cursor: 'pointer',
                    background:
                        openPopOver === id
                            ? 'linear-gradient(90deg, #205CFF 0%, #CD9BEA 100%)'
                            : undefined,
                    color: openPopOver === id ? 'white' : undefined,
                }}
                className="ai-summary-highlight"
                onClick={(e) => {
                    e.stopPropagation();
                    setPopOver?.(id);
                    fetchSummary();
                    track('HEALTH_SUMMARY_TAG_CLICK', {
                        cleanText,
                        patientId,
                    });
                }}
            >
                {cleanText}
            </span>

            {openPopOver === id && (
                <PatientSummaryPopUp
                    medicalProfile={medicalProfile}
                    medicationArray={medicationArray}
                    vitalsArray={vitalsArray}
                    onFeedback={onFeedback}
                    clear={clear}
                    patientId={patientId}
                    cleanText={cleanText}
                />
            )}
        </>
    );
};

const PatientSummaryPopUp: React.FC<{
    medicalProfile?: UpdatedMedicalProfile;
    medicationArray: MedicationArray[];
    vitalsArray?: VitalsArray[];
    onFeedback: (type: FeedbackType, input?: string) => void;
    clear: () => void;
    patientId: string;
    cleanText: string;
}> = ({
    medicalProfile,
    medicationArray,
    vitalsArray,
    onFeedback,
    clear,
    patientId,
    cleanText,
}) => {
    const eventListener = (event: Event) => {
        const patientSummaryDiv = document.getElementById('patient-summary-id');
        const targetElement = event.target as Node;

        if (!patientSummaryDiv?.contains(targetElement)) {
            clear();
        }
    };

    useEffect(() => {
        document.addEventListener('click', eventListener);

        return () => {
            document.removeEventListener('click', eventListener);
        };
    }, []);

    return (
        <div
            id="patient-summary-id"
            className="absolute max-h-90p right-308 top-0 z-50 flex flex-col w-360 bg-white shadow-900 rounded-8 overflow-hidden"
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <div
                className="flex items-center justify-between h-5 bg-darwin-accent-bright-blue-800 rounded-t-7"
                style={{
                    background: 'linear-gradient(90deg, #205CFF 0%, #CD9BEA 100%)',
                }}
            ></div>
            <div className="flex-1 break-all overflow-scroll hide-scrollbar text-16 px-12 py-16">
                <div>
                    <div className="space-y-8">
                        <div className="bg-white flex justify-between items-center text-16 font-700">
                            <p className="uppercase text-neutral-black truncate">
                                {medicalProfile?.condition_tag || cleanText}
                            </p>
                        </div>
                        <div className="text-13">{medicalProfile?.condition_overview}</div>
                    </div>
                </div>
                <p className="w-full flex justify-center my-12">
                    <p
                        className={`${
                            medicalProfile ? '' : 'ai_patient_summary__line_loader'
                        } h-1 rounded-full bg-darwin-neutral_new-200 w-full`}
                    >
                        {!medicalProfile && <span className="blue-line"></span>}
                    </p>
                </p>
                {medicalProfile ? (
                    <div className="space-y-16">
                        <div className="space-y-16">
                            {medicationArray?.map((m) => {
                                return (
                                    <div>
                                        <div className="space-y-4">
                                            <div className="flex space-x-8 text-12 font-400">
                                                <div className="flex-1 space-y-9">
                                                    {m?.diffDate && (
                                                        <div>
                                                            <div className="flex items-center justify-between pb-8">
                                                                <div className="flex items-center space-x-8 font-400">
                                                                    <p className="w-26 h-26 flex justify-center items-center text-12 font-700 bg-darwin-accent-medicines-pink-200 text-darwin-accent-medicines-pink-800 rounded-full">
                                                                        Mx
                                                                    </p>
                                                                    {m.type === 'current' ? (
                                                                        <span className="font-700 text-darwin-accent-medicines-pink-800">
                                                                            Since{' '}
                                                                            {moment(m.start).format(
                                                                                "DD MMM'YY",
                                                                            )}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="font-700 text-darwin-accent-medicines-pink-800">
                                                                            From{' '}
                                                                            {moment(m.start).format(
                                                                                "DD MMM'YY",
                                                                            )}{' '}
                                                                            to{' '}
                                                                            {moment(m.end).format(
                                                                                "DD MMM'YY",
                                                                            )}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <p className="flex items-center justify-between text-darwin-neutral-1000 font-400 text-13">
                                                                    <span className="text-darwin-neutrals-600">
                                                                        {m.diffDate}
                                                                    </span>
                                                                </p>
                                                            </div>

                                                            <div className="space-y-4">
                                                                {!m?.medicines?.length ? (
                                                                    <div>-</div>
                                                                ) : (
                                                                    m?.medicines?.map((m) => {
                                                                        return (
                                                                            <div className="flex items-center justify-between">
                                                                                <p className="-darwin-neutral_new-1000 text-13">
                                                                                    {m?.name}
                                                                                </p>
                                                                                <p className="-darwin-neutral_new-1000 text-13">
                                                                                    {m?.frequency}
                                                                                </p>
                                                                            </div>
                                                                        );
                                                                    })
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {vitalsArray?.map((v) => {
                            return (
                                <div>
                                    <div className="space-y-4">
                                        <div className="flex space-x-8 text-12 font-400">
                                            <div className="flex-1 space-y-9">
                                                <div>
                                                    <div className="flex items-center justify-between pb-8">
                                                        <div className="flex items-center space-x-8 font-400">
                                                            <div className="flex space-x-8 font-400">
                                                                <p className="w-26 h-26 flex cursor-pointer justify-center items-center text-12 font-700 bg-darwin-accent-investigations-ochre-100 rounded-full">
                                                                    <svg
                                                                        className="w-16 h-12 fill-white fill-darwin-accent-bright-yellow-900"
                                                                        viewBox="0 0 9 13"
                                                                        role="img"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                    >
                                                                        <path d="M5.21758 1.03906C5.03008 0.359375 4.0457 0.335938 3.83476 1.03906C2.75664 4.71875 0.412889 5.72656 0.412889 8.32812C0.412889 10.6484 2.24101 12.5 4.53789 12.5C6.81133 12.5 8.66289 10.6484 8.66289 8.32812C8.66289 5.72656 6.31914 4.74219 5.21758 1.03906ZM4.53789 11C3.08476 11 1.91289 9.82812 1.91289 8.39844C1.91289 8.21094 2.07695 8.02344 2.28789 8.02344C2.47539 8.02344 2.66289 8.21094 2.66289 8.39844C2.66289 9.45312 3.4832 10.25 4.53789 10.25C4.72539 10.25 4.91289 10.4375 4.91289 10.625C4.91289 10.8359 4.72539 11 4.53789 11Z"></path>
                                                                    </svg>
                                                                </p>
                                                            </div>
                                                            <span className="uppercase font-700 text-darwin-accent-investigations-ochre-800">
                                                                Vitals
                                                            </span>{' '}
                                                        </div>
                                                        <p className="flex items-center justify-between text-darwin-neutral-1000 font-400 text-13">
                                                            <span className="text-darwin-neutrals-600">
                                                                from{' '}
                                                                {moment(v.start).format(
                                                                    "DD MMM' YYYY",
                                                                )}
                                                            </span>
                                                        </p>
                                                    </div>
                                                    <div className="space-y-4">
                                                        {!v?.vitals?.length ? (
                                                            <div>-</div>
                                                        ) : (
                                                            v?.vitals?.map((v) => {
                                                                return (
                                                                    <div className="flex space-x-12 items-center justify-between ">
                                                                        <p className="-darwin-neutral_new-1000 text-13">
                                                                            {v?.name}
                                                                        </p>
                                                                        <span className="-darwin-neutral_new-1000 text-13 space-x-8">
                                                                            <span className="font-700">
                                                                                {v?.progression}{' '}
                                                                                {v?.unit}
                                                                            </span>
                                                                            <span>
                                                                                <i>
                                                                                    {
                                                                                        v?.interpretation
                                                                                    }
                                                                                </i>
                                                                            </span>
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-13 ai_patient_summary_loading">
                        Generating smart condition summary
                    </div>
                )}
            </div>
            <div
                className="flex items-center justify-between space-x-8 p-8 rounded-b-7"
                style={{
                    background: 'linear-gradient(90deg, #205CFF 0%, #CD9BEA 100%)',
                }}
            >
                <div className="flex items-center space-x-4">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                    >
                        <circle cx="8.61533" cy="8.07602" r="5.38462" fill="white" />
                        <g filter="url(#filter0_b_45_3050)">
                            <circle
                                cx="3.76923"
                                cy="3.76923"
                                r="3.76923"
                                fill="white"
                                fill-opacity="0.6"
                            />
                        </g>
                        <defs>
                            <filter
                                id="filter0_b_45_3050"
                                x="-1.07692"
                                y="-1.07692"
                                width="9.69242"
                                height="9.69291"
                                filterUnits="userSpaceOnUse"
                                color-interpolation-filters="sRGB"
                            >
                                <feFlood flood-opacity="0" result="BackgroundImageFix" />
                                <feGaussianBlur in="BackgroundImageFix" stdDeviation="0.538462" />
                                <feComposite
                                    in2="SourceAlpha"
                                    operator="in"
                                    result="effect1_backgroundBlur_45_3050"
                                />
                                <feBlend
                                    mode="normal"
                                    in="SourceGraphic"
                                    in2="effect1_backgroundBlur_45_3050"
                                    result="shape"
                                />
                            </filter>
                        </defs>
                    </svg>
                    <span className="text-11 text-white whitespace-nowrap">
                        Powered by Eka DocAssist AI
                    </span>
                </div>

                <div className="flex items-center space-x-6">
                    <button
                        className="p-4 bg-white rounded-4 cursor-pointer"
                        onClick={() => {
                            onFeedback('positive');
                        }}
                    >
                        <svg
                            className="w-14 h-13 fill-darwin-accent-bright-blue-800"
                            viewBox="0 0 14 12"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M8.70117 0.513672C9.69141 0.792969 10.25 1.80859 9.9707 2.77344L9.81836 3.28125C9.74219 3.56055 9.64062 3.83984 9.51367 4.09375H11.875C12.7637 4.09375 13.5 4.83008 13.5 5.71875C13.5 6.17578 13.2969 6.58203 13.0176 6.88672C13.0684 7.03906 13.0938 7.19141 13.0938 7.36914C13.0938 7.87695 12.8398 8.35938 12.4336 8.66406C12.459 8.76562 12.4844 8.86719 12.4844 8.99414C12.4844 9.50195 12.2305 9.95898 11.875 10.2637C11.8242 11.127 11.1133 11.8379 10.25 11.8379H7.76172C7.20312 11.8379 6.64453 11.6602 6.1875 11.3555L4.61328 10.3145C4.33398 10.1113 4.25781 9.73047 4.46094 9.45117C4.63867 9.17188 5.01953 9.0957 5.29883 9.29883L6.87305 10.3398C7.12695 10.5176 7.43164 10.5938 7.76172 10.5938H10.25C10.4531 10.5938 10.6562 10.416 10.6562 10.1875C10.6562 10.1621 10.6309 10.1113 10.6309 10.0859C10.5547 9.78125 10.707 9.47656 10.9863 9.34961C11.1387 9.29883 11.2656 9.14648 11.2656 8.96875C11.2656 8.89258 11.2148 8.79102 11.1641 8.74023C11.0371 8.5625 11.0117 8.33398 11.0879 8.13086C11.1387 7.92773 11.3164 7.80078 11.5449 7.75C11.7227 7.72461 11.875 7.54688 11.875 7.34375C11.875 7.24219 11.8242 7.16602 11.7734 7.08984C11.6211 6.9375 11.5703 6.73438 11.6211 6.53125C11.6719 6.32812 11.8242 6.17578 12.002 6.09961C12.1543 6.04883 12.2812 5.89648 12.2812 5.71875C12.2812 5.51562 12.0781 5.3125 11.875 5.3125H8.47266C8.24414 5.3125 8.04102 5.18555 7.93945 4.98242C7.83789 4.7793 7.83789 4.55078 7.96484 4.37305C8.26953 3.94141 8.49805 3.45898 8.65039 2.95117L8.80273 2.44336C8.87891 2.11328 8.70117 1.7832 8.37109 1.68164C8.04102 1.60547 7.71094 1.7832 7.60938 2.11328L7.48242 2.62109C7.33008 3.12891 7.07617 3.58594 6.7207 3.99219L5.40039 5.41406C5.17188 5.66797 4.79102 5.69336 4.53711 5.46484C4.30859 5.23633 4.2832 4.85547 4.51172 4.60156L5.80664 3.17969C6.03516 2.92578 6.21289 2.62109 6.31445 2.29102L6.44141 1.7832C6.7207 0.792969 7.73633 0.234375 8.70117 0.513672ZM1.3125 4.5H2.9375C3.36914 4.5 3.75 4.88086 3.75 5.3125V11C3.75 11.457 3.36914 11.8125 2.9375 11.8125H1.3125C0.855469 11.8125 0.5 11.457 0.5 11V5.3125C0.5 4.88086 0.855469 4.5 1.3125 4.5Z" />
                        </svg>
                    </button>
                    <FeedbackPopover
                        onFeedback={onFeedback}
                        removePopupContainer
                        from="patient-summary-magic-box"
                        patientId={patientId}
                    >
                        <button className="p-4 bg-white rounded-4 cursor-pointer">
                            <svg
                                className="w-14 h-13 fill-darwin-accent-bright-blue-800"
                                viewBox="0 0 14 13"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M8.70117 11.7617C7.73633 12.041 6.7207 11.457 6.44141 10.4922L6.31445 9.98438C6.21289 9.6543 6.03516 9.34961 5.80664 9.0957L4.51172 7.67383C4.2832 7.41992 4.30859 7.03906 4.53711 6.81055C4.79102 6.58203 5.17188 6.60742 5.40039 6.86133L6.7207 8.2832C7.07617 8.66406 7.33008 9.14648 7.48242 9.6543L7.60938 10.1621C7.71094 10.4922 8.04102 10.6699 8.37109 10.5938C8.70117 10.4922 8.87891 10.1621 8.80273 9.83203L8.65039 9.32422C8.49805 8.81641 8.26953 8.33398 7.96484 7.90234C7.83789 7.72461 7.83789 7.4707 7.93945 7.26758C8.04102 7.06445 8.24414 6.9375 8.47266 6.9375H11.875C12.0781 6.9375 12.2812 6.75977 12.2812 6.53125C12.2812 6.37891 12.1543 6.22656 12.002 6.15039C11.8242 6.09961 11.6719 5.92188 11.6211 5.74414C11.5703 5.54102 11.6211 5.33789 11.7734 5.18555C11.8242 5.10938 11.875 5.0332 11.875 4.90625C11.875 4.72852 11.7227 4.55078 11.5449 4.52539C11.3164 4.47461 11.1387 4.32227 11.0879 4.14453C11.0117 3.94141 11.0371 3.71289 11.1641 3.53516C11.2148 3.48438 11.2656 3.38281 11.2656 3.28125C11.2656 3.12891 11.1387 2.97656 10.9863 2.90039C10.707 2.79883 10.5547 2.49414 10.6309 2.18945C10.6309 2.13867 10.6562 2.11328 10.6562 2.0625C10.6562 1.85938 10.4531 1.65625 10.25 1.65625H7.76172C7.43164 1.65625 7.12695 1.75781 6.87305 1.93555L5.29883 2.97656C5.01953 3.17969 4.63867 3.10352 4.46094 2.79883C4.25781 2.51953 4.33398 2.16406 4.61328 1.96094L6.1875 0.919922C6.64453 0.615234 7.20312 0.4375 7.76172 0.4375H10.25C11.1133 0.4375 11.8242 1.14844 11.875 2.01172C12.2305 2.31641 12.4844 2.77344 12.4844 3.28125C12.4844 3.4082 12.459 3.50977 12.4336 3.61133C12.8398 3.91602 13.0938 4.37305 13.0938 4.90625C13.0938 5.08398 13.0684 5.23633 13.0176 5.38867C13.2969 5.69336 13.5 6.09961 13.5 6.53125C13.5 7.44531 12.7637 8.15625 11.875 8.15625H9.51367C9.64062 8.43555 9.74219 8.71484 9.81836 8.99414L9.9707 9.50195C10.25 10.4668 9.69141 11.4824 8.70117 11.7617ZM1.3125 9.375C0.855469 9.375 0.5 9.01953 0.5 8.5625V2.875C0.5 2.44336 0.855469 2.0625 1.3125 2.0625H2.9375C3.36914 2.0625 3.75 2.44336 3.75 2.875V8.5625C3.75 9.01953 3.36914 9.375 2.9375 9.375H1.3125Z" />
                            </svg>
                        </button>
                    </FeedbackPopover>
                </div>
            </div>
        </div>
    );
};
